import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AttentionSignal,
  PlatformSiteHealth,
  PlatformSystemOverview,
  PlatformUpdate,
  getPlatformUpdates,
  getSiteHealth,
  getSystemOverview,
} from '../../api/platform/system';
import SiteUptimeHistory from './SiteUptimeHistory';
import SystemUpdateManager from './SystemUpdateManager';
import { EzeeEconomicsPanel } from './EzeeEconomicsPanel';
import { get as apiGet, put as apiPut, post as apiPost } from '../../api/coreApi';
import './SystemOperationsPage.css';
import './SystemTabs.css';

type LoadState = 'loading' | 'ready' | 'error';
type SystemTab = 'overview' | 'operations' | 'uptime' | 'updates' | 'ezee';

const SYSTEM_TABS: Array<{ id: SystemTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'operations', label: 'Operations' },
  { id: 'uptime', label: 'Uptime' },
  { id: 'updates', label: 'Updates' },
  { id: 'ezee', label: 'Ezee AI' },
];

const formatNumber = (value?: number | null) => new Intl.NumberFormat().format(Number(value || 0));

const formatHours = (value?: number | null) => {
  if (value == null) return '—';
  if (value < 24) return `${Math.round(value)}h`;
  return `${(value / 24).toFixed(value >= 72 ? 0 : 1)}d`;
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date);
};

const formatTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const humanize = (value?: string | null) =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const initials = (name?: string | null) =>
  String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

const MetricCard: React.FC<{
  label: string;
  value: React.ReactNode;
  detail?: string;
  tone?: 'default' | 'critical' | 'warning' | 'healthy';
}> = ({ label, value, detail, tone = 'default' }) => (
  <div className={`system-metric system-metric--${tone}`}>
    <span className="system-metric__label">{label}</span>
    <strong className="system-metric__value">{value}</strong>
    {detail ? <span className="system-metric__detail">{detail}</span> : null}
  </div>
);

const SignalRow: React.FC<{ signal: AttentionSignal }> = ({ signal }) => {
  const detail = signal.metadata || {};
  const assignees = detail.assignees || [];
  const entityLabel = `${humanize(signal.entityType)}${signal.entityId != null ? ` #${signal.entityId}` : ''}`;
  const detailChips = [detail.status, detail.urgency, detail.priority, detail.taskType]
    .filter(Boolean)
    .map((value) => humanize(String(value)));

  return (
    <div className="system-signal">
      <span className={`system-signal__dot system-signal__dot--${signal.severity}`} aria-hidden="true" />
      <div className="system-signal__body">
        <div className="system-signal__title-row">
          <strong>{signal.title}</strong>
          <span className={`system-pill system-pill--${signal.severity}`}>{signal.severity}</span>
        </div>

        <div className="system-signal__entity-row">
          <strong className="system-signal__entity-title">{detail.entityTitle || entityLabel}</strong>
          <span className="system-signal__entity-id">{entityLabel}</span>
        </div>

        {signal.description ? <p className="system-signal__description">{signal.description}</p> : null}

        <div className="system-signal__context">
          {detailChips.map((chip) => (
            <span className="system-signal__context-chip" key={chip}>{chip}</span>
          ))}
          {detail.lastActivityAt ? <span>Last activity {formatTime(detail.lastActivityAt)}</span> : null}
          {detail.deadlineAt ? <span>Due {formatTime(detail.deadlineAt)}</span> : null}
        </div>

        <div className="system-signal__assignment">
          <span className="system-signal__assignment-label">Assigned To</span>
          {assignees.length ? (
            <div className="system-signal__assignees">
              {assignees.map((assignee) => (
                <div className="system-assignee" key={`${assignee.kind || 'assignee'}-${assignee.id}`} title={assignee.email || assignee.name}>
                  {assignee.avatarUrl ? (
                    <img className="system-assignee__avatar" src={assignee.avatarUrl} alt="" />
                  ) : (
                    <span className="system-assignee__avatar system-assignee__avatar--fallback" aria-hidden="true">
                      {initials(assignee.name)}
                    </span>
                  )}
                  <span className="system-assignee__name">{assignee.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <span className="system-signal__unassigned">Unassigned</span>
          )}
        </div>

        {signal.reasons?.length ? (
          <div className="system-signal__meta">Why flagged · {signal.reasons.map(humanize).join(' · ')}</div>
        ) : null}
      </div>
      <span className="system-signal__score" title="Attention score">{signal.score}</span>
    </div>
  );
};

const EmptyState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="system-empty">{children}</div>
);

type EzeeGovernance = {
  platformRoleRequired: boolean; allTaggedModelCallsRequireMetering: boolean;
  improvementMeteredOnly: boolean; actionRoutingEnabled: boolean;
  actionMeteredOnly: boolean; legacyActionsMasterGuarded: boolean;
  providerInvoiceComparisonsEnabled: boolean;
  legacyProviderSettingsServerLocked: boolean;
  actionEngineServerEnabled?: boolean;
};
const defaultGovernance: EzeeGovernance = {
  platformRoleRequired:false, allTaggedModelCallsRequireMetering:false,
  improvementMeteredOnly:false, actionRoutingEnabled:false,
  actionMeteredOnly:false, legacyActionsMasterGuarded:false,
  providerInvoiceComparisonsEnabled:false,
  legacyProviderSettingsServerLocked:false,
};
type RoutingSettings = { enabled: boolean; strategy: 'economy' | 'balanced' | 'quality'; maxTier: 'economy' | 'standard' | 'premium'; allowFallback: boolean; monthlyBudgetUsd: number };
type LightweightStatus = { serverEnabled: boolean; approvedEconomyTasks: string[] };
type SelfHostedStatus = { serverEnabled: boolean; configuredEndpointCount: number; enabledEndpointCount: number; readyEndpointCount: number };
type RoutingLimits = { serverEnabled: boolean; maxMonthlyBudgetUsd: number; maxTier: 'economy' | 'standard' | 'premium' };
type ModelInfo = { id: string; provider: string; model: string; tier: string; tasks: string[]; dataClasses: string[]; inputUsdPerMillion: number; outputUsdPerMillion: number; qualityScore: number };
type RoutingUsage = { periodKey: string; estimatedSpentUsd: number; reservedUsd: number; recent: Array<{ taskType: string; modelId: string; estimatedCostUsd: number; costBasis?: string; status: string; createdAt: string }> };
const defaultRouting: RoutingSettings = { enabled: false, strategy: 'balanced', maxTier: 'standard', allowFallback: false, monthlyBudgetUsd: 0 };
type ImprovementSettings = { mode: string; reflectionEnabled: boolean; evaluationsEnabled: boolean; allowedSafeActions: string[] };
type NervousState = { enabled: boolean; emergencyStop: boolean; reason: string | null };
type NervousResponse = { state: NervousState; active: boolean; deployment?: { runtimeArmed: boolean; backgroundWorkersArmed: boolean } };
type EmailSettings = { enabled: boolean; recipientEmail: string; introducedAt: string | null; lastSentAt: string | null; messageCount: number };
const EzeeEmailPanel: React.FC = () => {
  const [instance, setInstance] = useState('elite-promo-marketing');
  const [settings, setSettings] = useState<EmailSettings>({ enabled: false, recipientEmail: '', introducedAt: null, lastSentAt: null, messageCount: 0 });
  const [improvements, setImprovements] = useState<ImprovementSettings>({ mode: 'observe', reflectionEnabled: false, evaluationsEnabled: false, allowedSafeActions: [] });
  const [nervous, setNervous] = useState<NervousState>({ enabled: false, emergencyStop: false, reason: null });
  const [runtimeArmed, setRuntimeArmed] = useState(false);
  const [workersArmed, setWorkersArmed] = useState(false);
  const [routing, setRouting] = useState<RoutingSettings>(defaultRouting);
  const [governance,setGovernance] = useState<EzeeGovernance>(defaultGovernance);
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [lightweight, setLightweight] = useState<LightweightStatus>({ serverEnabled:false, approvedEconomyTasks:[] });
  const [selfHosted, setSelfHosted] = useState<SelfHostedStatus>({ serverEnabled: false, configuredEndpointCount: 0, enabledEndpointCount: 0, readyEndpointCount: 0 });
  const [routingLimits, setRoutingLimits] = useState<RoutingLimits>({ serverEnabled: false, maxMonthlyBudgetUsd: 0, maxTier: 'standard' });
  const [routingUsage, setRoutingUsage] = useState<RoutingUsage | null>(null);
  const [loadedInstance, setLoadedInstance] = useState('');
  const [ezeeView, setEzeeView] = useState<'controls' | 'economics'>('controls');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const ready = !!loadedInstance && loadedInstance === instance.trim();
  const endpoint = `/ai/instances/${encodeURIComponent(instance.trim())}/email-mode`;
  const routingEndpoint = `/ai/instances/${encodeURIComponent(instance.trim())}/model-routing`;
  const improvementsEndpoint = `/ai/instances/${encodeURIComponent(instance.trim())}/improvements/autonomy`;
  const controlsEndpoint = `/ai/instances/${encodeURIComponent(instance.trim())}/nervous-system`;
  const act = async (kind: 'load' | 'save' | 'send' | 'start' | 'pause' | 'stop' | 'resume' | 'save-improvements' | 'save-routing') => {
    setBusy(true); setStatus('');
    try {
      if (kind !== 'load' && !ready) {
        setStatus('Load this instance before changing Ezee controls.');
        return;
      }
      if (kind === 'start') {
        if (!runtimeArmed) { setStatus('Ezee runtime is disarmed by the server. A developer must explicitly arm it before activation.'); return; }
        if (!window.confirm(`Activate Ezee master for ${instance.trim()}? Capabilities and background jobs remain independently gated.`)) return;
      }
      if (kind === 'send') {
        if (!runtimeArmed || !nervous.enabled || nervous.emergencyStop || !settings.enabled) {
          setStatus('Ezee email delivery remains disabled until the runtime, instance master, email breaker and Email Mode have all been enabled.'); return;
        }
        if (!window.confirm(`Send Ezee email to ${settings.recipientEmail} for ${instance.trim()} now? This action cannot be undone.`)) return;
      }
      if (kind === 'save-routing') {
        const res = await apiPut<{settings: RoutingSettings}>(routingEndpoint, routing);
        setRouting(res.settings);
        try {
          const usage = await apiGet<{usage: RoutingUsage}>(routingEndpoint + '/usage');
          setRoutingUsage(usage.usage);
        } catch {
          setRoutingUsage(null);
        }
        setStatus('Routing oversight policy saved. Developer-owned models and credentials were not changed.');
      } else if (kind === 'save-improvements') {
        const res = await apiPut<{settings: ImprovementSettings}>(improvementsEndpoint, improvements);
        setImprovements(res.settings); setStatus('Improvement controls saved.');
      } else if (['start','pause','stop','resume'].includes(kind)) {
        const desired = kind === 'start'
          ? { enabled: true, emergencyStop: false }
          : kind === 'resume' ? { enabled: false, emergencyStop: false }
          : kind === 'stop' ? { enabled: false, emergencyStop: true, reason: 'Emergency stop from Command Centre' } : { enabled: false };
        const result = await apiPut<{ state: NervousState }>(controlsEndpoint, desired);
        setNervous(result.state);
        setStatus(kind === 'stop' ? 'Emergency stop activated and Ezee disabled.' : kind === 'pause' ? 'Ezee paused.' : kind === 'resume' ? 'Emergency stop cleared; Ezee master remains OFF.' : 'Ezee master activated. Other breakers remain independently OFF.');
      } else if (kind === 'save') {
        const res = await apiPut<{ settings: EmailSettings }>(endpoint, { enabled: settings.enabled, recipientEmail: settings.recipientEmail });
        setSettings(res.settings); setStatus('Settings saved.');
      } else {
        if (kind === 'send') {
          const res = await apiPost<{ subject: string; recipientEmail: string }>(endpoint + '/send', {});
          setStatus(`Sent ${res.subject} to ${res.recipientEmail}.`);
        }
        const [res, control, improvement, modelPolicy] = await Promise.all([
          apiGet<{settings: EmailSettings}>(endpoint),
          apiGet<NervousResponse>(controlsEndpoint),
          apiGet<{settings: ImprovementSettings}>(improvementsEndpoint),
          apiGet<{settings: RoutingSettings; models: ModelInfo[]; limits: RoutingLimits; selfHosted: SelfHostedStatus; lightweight: LightweightStatus; governance: EzeeGovernance}>(routingEndpoint),
        ]);
        setRouting(modelPolicy.settings);
        setGovernance(modelPolicy.governance || defaultGovernance);
        setModels(modelPolicy.models);
        setRoutingLimits(modelPolicy.limits);
        setSelfHosted(modelPolicy.selfHosted);
        setLightweight(modelPolicy.lightweight);
        setLoadedInstance(instance.trim());
        try {
          const cost = await apiGet<{usage: RoutingUsage}>(routingEndpoint + '/usage');
          setRoutingUsage(cost.usage);
        } catch {
          setRoutingUsage(null);
          setStatus('Ezee controls loaded, but model usage reporting is unavailable. Check database migrations.');
        }
        setImprovements(improvement.settings);
        setSettings(res.settings);
        setNervous(control.state);
        setRuntimeArmed(control.deployment?.runtimeArmed === true);
        setWorkersArmed(control.deployment?.backgroundWorkersArmed === true);
      }
    } catch (err) { setStatus(err instanceof Error ? err.message : 'Unable to complete request.'); }
    finally { setBusy(false); }
  };
  return <section className="system-card" style={{padding:24, maxWidth:1180}}>
    <h2>Ezee AI — Command Centre</h2>
    <p>Instance-level AI oversight: activation, model routing, email and improvement controls, with a separate read-only economics view of recorded routed-model usage.</p>
    <label htmlFor="ezee-instance">Instance slug</label>
    <input id="ezee-instance" value={instance} onChange={e => setInstance(e.target.value)} style={{display:'block',width:'100%',marginBottom:12}} />
    <button type="button" disabled={busy || !instance.trim()} onClick={() => void act('load')}>Load</button>
    <nav aria-label="Ezee AI sections" style={{display:'flex',gap:8,marginTop:18,marginBottom:12,flexWrap:'wrap'}}>
      <button type="button" onClick={()=>setEzeeView('controls')}
        aria-current={ezeeView==='controls'?'page':undefined}
        style={{padding:'9px 14px',borderRadius:9,border:'1px solid #d8e5ec',
          background:ezeeView==='controls'?'#e7f4ff':'white',fontWeight:700,cursor:'pointer'}}>
        Controls & settings
      </button>
      <button type="button" onClick={()=>setEzeeView('economics')}
        aria-current={ezeeView==='economics'?'page':undefined}
        style={{padding:'9px 14px',borderRadius:9,border:'1px solid #d8e5ec',
          background:ezeeView==='economics'?'#e7f4ff':'white',fontWeight:700,cursor:'pointer'}}>
        AI Economics
      </button>
    </nav>
    {ezeeView === 'controls' ? <>
    <div style={{marginTop:18, marginBottom:18, padding:16, border:'1px solid #ddd', borderRadius:8}}>
      <h3>Ezee master controls</h3>
      <p>Deployment runtime: <strong>{runtimeArmed ? 'Armed' : 'OFF — cold start'}</strong> · Background jobs: <strong>{workersArmed ? 'Armed' : 'OFF'}</strong>. These are developer-controlled switches.</p>
      <p>Master state: <strong>{nervous.emergencyStop ? 'EMERGENCY STOP' : runtimeArmed && nervous.enabled ? 'ACTIVE' : 'OFF / PAUSED'}</strong>. Emergency stop and individual capability breakers override activation.</p>
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        <button type="button" disabled={!ready || busy || !runtimeArmed || nervous.emergencyStop} onClick={() => void act('start')}>Activate Ezee</button>
        <button type="button" disabled={!ready || busy || !nervous.enabled} onClick={() => void act('pause')}>Pause Ezee</button>
        <button type="button" disabled={!ready || busy} onClick={() => void act('stop')}>Emergency stop</button>
        <button type="button" disabled={!ready || busy || !nervous.emergencyStop} onClick={() => void act('resume')}>Clear emergency stop (keep OFF)</button>
      </div>
    </div>
    <div style={{marginTop:18, marginBottom:18, padding:16, border:'1px solid #ddd', borderRadius:8}}>
      <h3>Improvement autonomy</h3>
      <label htmlFor="ezee-autonomy">Mode</label>
      <select id="ezee-autonomy" value={improvements.mode} onChange={e => setImprovements({...improvements, mode:e.target.value})} style={{display:'block',marginBottom:12}}>
        <option value="observe">Observe only</option><option value="recommend">Recommend</option><option value="simulate">Simulate</option><option value="prepare">Prepare for review</option><option value="engineering_proposal">Engineering proposal</option><option value="auto_safe">Auto-safe (requires explicit allowlist)</option>
      </select>
      <label style={{display:'block'}}><input type="checkbox" checked={improvements.reflectionEnabled} onChange={e => setImprovements({...improvements,reflectionEnabled:e.target.checked})} /> Enable reflection</label>
      <label style={{display:'block'}}><input type="checkbox" checked={improvements.evaluationsEnabled} onChange={e => setImprovements({...improvements,evaluationsEnabled:e.target.checked})} /> Enable evaluations</label>
      <p>Autonomous safe-action allowlist: {improvements.allowedSafeActions?.length || 0} approved actions. No automatic actions are permitted without an allowlist.</p>
      <button type="button" disabled={!ready || busy} onClick={() => void act('save-improvements')}>Save improvement controls</button>
    </div>
    <div style={{marginTop:18, marginBottom:18, padding:16, border:'1px solid #ddd', borderRadius:8}}>
      <h3>Model intelligence</h3>
      <p>Developers configure the approved model catalog, provider keys, prices, supported tasks and data classifications on the server. Overseers can choose how Ezee uses those approved models.</p>
      <p><strong>{models.length} developer-approved models</strong> · {routing.enabled ? 'Smart routing enabled' : 'Smart routing disabled'}
        {' '}· Server: {routingLimits.serverEnabled ? 'Available' : 'Developer lock'}</p>
      <p>Developer ceilings: maximum {routingLimits.maxTier} tier; ${routingLimits.maxMonthlyBudgetUsd.toFixed(2)} USD per month.</p>
      <p><strong>Self-hosted inference:</strong> {selfHosted.serverEnabled ? 'Developer-enabled' : 'Disabled by developers'} · {selfHosted.readyEndpointCount} ready endpoint(s), {selfHosted.enabledEndpointCount} enabled from {selfHosted.configuredEndpointCount} configured. No local addresses or tokens are exposed to Command Centre.</p>
      <p><strong>Lightweight requests:</strong> {lightweight.serverEnabled ? 'Developer-enabled' : 'Disabled by developers'} · {lightweight.approvedEconomyTasks.length} of 3 approved economy categories. Casual chat, general terms and self-contained text edits use the shortest path only when an eligible model is available. Local hosting is not required.</p>
      <details style={{margin:'12px 0',padding:12,border:'1px solid #dce6ee',borderRadius:10}}>
        <summary style={{fontWeight:700,cursor:'pointer'}}>Developer governance status (read-only)</summary>
        <p style={{fontSize:12}}>These server-owned safeguards are visible here but cannot be changed through browser requests.</p>
        <ul style={{fontSize:12,lineHeight:1.8}}>
          <li>Dedicated platform role requirement: <strong>{governance.platformRoleRequired?'Required':'Staged — instance admin checks active'}</strong></li>
          <li>All tagged Ezee inference must be metered: <strong>{governance.allTaggedModelCallsRequireMetering?'Required':'Not yet enforced'}</strong></li>
          <li>Improvement inference metered-only: <strong>{governance.improvementMeteredOnly?'Required':'Not yet enforced'}</strong></li>
          <li>CRM and daily-operation AI routing: <strong>{governance.actionRoutingEnabled?'Developer-enabled':'Legacy provider remains in use'}</strong></li>
          <li>CRM and daily-operation AI metered-only: <strong>{governance.actionMeteredOnly?'Required':'Not yet enforced'}</strong></li>
          <li>Legacy AI actions checked against master stop: <strong>{governance.legacyActionsMasterGuarded?'Required':'Legacy compatibility mode'}</strong></li>
          <li>Dedicated vendor invoice snapshots: <strong>{governance.providerInvoiceComparisonsEnabled?'Enabled':'Off'}</strong></li>
          <li>Legacy provider settings editing: <strong>{governance.legacyProviderSettingsServerLocked?'Locked by developers':'Legacy administrator editing remains available'}</strong></li>
          <li>Ezee Action Engine (sales workflow): <strong>{governance.actionEngineServerEnabled?'Developer-enabled (staging review required)':'Off — no workflow execution'}</strong></li>
        </ul>
      </details>
      <label htmlFor="ezee-routing-strategy">Routing strategy</label>
      <select id="ezee-routing-strategy" disabled={!ready || busy} value={routing.strategy}
        onChange={e => setRouting({...routing, strategy: e.target.value as RoutingSettings['strategy']})}
        style={{display:'block',marginBottom:12}}>
        <option value="economy">Economy — lowest estimated price</option>
        <option value="balanced">Balanced — quality and cost</option>
        <option value="quality">Quality — highest approved quality rating</option>
      </select>
      <label htmlFor="ezee-max-tier">Maximum model tier</label>
      <select id="ezee-max-tier" disabled={!ready || busy} value={routing.maxTier}
        onChange={e => setRouting({...routing, maxTier: e.target.value as RoutingSettings['maxTier']})}
        style={{display:'block',marginBottom:12}}>
        <option value="economy">Economy</option>
        <option value="standard" disabled={routingLimits.maxTier === 'economy'}>Standard</option>
        <option value="premium" disabled={routingLimits.maxTier !== 'premium'}>Premium (approved models only)</option>
      </select>
      <label htmlFor="ezee-monthly-budget">Estimated inference budget in USD, per UTC calendar month</label>
      <input id="ezee-monthly-budget" type="number" min={0} max={routingLimits.maxMonthlyBudgetUsd} step={1}
        disabled={!ready || busy} value={routing.monthlyBudgetUsd}
        onChange={e => setRouting({...routing, monthlyBudgetUsd: Number(e.target.value)})}
        style={{display:'block',width:160,marginBottom:12}} />
      <label style={{display:'block'}}>
        <input type="checkbox" disabled={!ready || busy || !routingLimits.serverEnabled || models.length===0} checked={routing.enabled}
          onChange={e=>setRouting({...routing,enabled:e.target.checked})} /> Enable smart model routing
      </label>
      <label style={{display:'block'}}>
        <input type="checkbox" disabled={!ready || busy} checked={routing.allowFallback}
          onChange={e=>setRouting({...routing,allowFallback:e.target.checked})} /> Allow fallback to another eligible model after temporary provider errors
      </label>
      <p>Monthly estimated cost: <strong>${Number(routingUsage?.estimatedSpentUsd || 0).toFixed(2)} USD</strong>
        {' '}· Reserved: ${Number(routingUsage?.reservedUsd || 0).toFixed(2)} USD</p>
      <p style={{fontSize:12}}>Costs are estimates from developer-maintained prices and provider token usage, not invoice totals. Failed/uncertain attempts conservatively retain their estimated budget charge. Routing never expands user privileges or bypasses Ezee's emergency stop. Monthly limits apply to routed calls only.</p>
      {routing.monthlyBudgetUsd > 0 && (Number(routingUsage?.estimatedSpentUsd || 0) + Number(routingUsage?.reservedUsd || 0)) >= routing.monthlyBudgetUsd * 0.8 ?
        <p role="alert">Estimated routed-model usage has reached at least 80% of the configured monthly budget. Review usage before allowing additional work.</p> : null}
      <button type="button" disabled={!ready || busy || (routing.enabled && (!routingLimits.serverEnabled || !models.length || routing.monthlyBudgetUsd <= 0 || routing.monthlyBudgetUsd > routingLimits.maxMonthlyBudgetUsd))}
        onClick={() => void act('save-routing')}>Save model controls</button>
      {models.length ? <details style={{marginTop:12}}>
        <summary>Developer-approved models</summary>
        {models.map(m => <p key={m.id} style={{fontSize:12}}><strong>{m.id}</strong> — {m.provider} / {m.tier}. Quality rating {m.qualityScore}/100. Eligible tasks: {m.tasks.join(', ')}.</p>)}
      </details> : <p>No models have been configured by developers. Smart routing cannot be activated.</p>}
      {routingUsage?.recent?.length ? <details style={{marginTop:12}}>
        <summary>Recent model usage</summary>
        {routingUsage.recent.slice(0,10).map((u,i) => <p key={u.createdAt + i} style={{fontSize:12}}>
          {u.createdAt}: {u.taskType} / {u.modelId} — {u.status}, estimated ${Number(u.estimatedCostUsd).toFixed(4)} ({u.costBasis || 'cost basis unavailable'})
        </p>)}
      </details> : null}
    </div>
    <div style={{marginTop:18}}>
      <label htmlFor="ezee-recipient">Recipient email</label>
      <input id="ezee-recipient" type="email" value={settings.recipientEmail} onChange={e => setSettings({...settings,recipientEmail:e.target.value})} style={{display:'block',width:'100%',marginBottom:12}} />
      <label><input type="checkbox" checked={settings.enabled} disabled={!ready || busy || (!runtimeArmed && !settings.enabled)} onChange={e => setSettings({...settings,enabled:e.target.checked})} /> Enable Ezee email mode</label>
      <p>Messages sent: {settings.messageCount} · First introduction: {settings.introducedAt || 'Not yet'} · Last sent: {settings.lastSentAt || 'Never'}</p>
      <div style={{display:'flex',gap:12}}>
        <button type="button" disabled={!ready || busy} onClick={() => void act('save')}>Save settings</button>
        <button type="button" disabled={!ready || busy || !runtimeArmed || !nervous.enabled || nervous.emergencyStop || !settings.enabled || !settings.recipientEmail} onClick={() => void act('send')}>Send update now</button>
      </div>
    </div>
    </> : <EzeeEconomicsPanel instanceUniqueId={ready?loadedInstance:null} />}
    {status && <p role="status">{status}</p>}
  </section>;
};

const SystemOperationsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<SystemTab>('overview');
  const [overview, setOverview] = useState<PlatformSystemOverview | null>(null);
  const [health, setHealth] = useState<PlatformSiteHealth | null>(null);
  const [updates, setUpdates] = useState<PlatformUpdate[]>([]);
  const [overviewState, setOverviewState] = useState<LoadState>('loading');
  const [healthState, setHealthState] = useState<LoadState>('loading');
  const [updatesState, setUpdatesState] = useState<LoadState>('loading');
  const [refreshing, setRefreshing] = useState(false);

  const loadPublishedUpdates = useCallback(async () => {
    setUpdatesState('loading');
    try {
      setUpdates(await getPlatformUpdates(8));
      setUpdatesState('ready');
    } catch {
      setUpdatesState('error');
    }
  }, []);

  const load = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);

    const overviewRequest = getSystemOverview(30)
      .then((value) => {
        setOverview(value);
        setOverviewState('ready');
      })
      .catch(() => setOverviewState('error'));

    const healthRequest = getSiteHealth()
      .then((value) => {
        setHealth(value);
        setHealthState('ready');
      })
      .catch(() => setHealthState('error'));

    await Promise.allSettled([overviewRequest, healthRequest, loadPublishedUpdates()]);
    if (manual) setRefreshing(false);
  }, [loadPublishedUpdates]);

  useEffect(() => {
    load();
  }, [load]);

  const criticalSignals = useMemo(
    () => overview?.attention.filter((signal) => signal.severity === 'critical').length || 0,
    [overview]
  );

  const warningSignals = useMemo(
    () => overview?.attention.filter((signal) => signal.severity === 'warning').length || 0,
    [overview]
  );

  const healthTone = health?.summary.overallStatus || 'default';
  const ticketChart = overview?.tickets.daily || [];
  const networkChart = overview?.coreNetwork.daily || [];

  return (
    <div className="system-page">
      <section className="system-page__hero">
        <div>
          <div className="system-page__eyebrow">Platform operations</div>
          <h1>System</h1>
          <p>Live operating signals across Elite Core, tickets, Core Network, and platform health.</p>
        </div>
        <div className="system-page__hero-actions">
          <span className="system-page__last-check">
            {health?.checkedAt ? `Checked ${formatTime(health.checkedAt)}` : 'Live checks'}
          </span>
          <button type="button" className="system-refresh" onClick={() => load(true)} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </section>

      <nav className="system-tabs" aria-label="System sections">
        {SYSTEM_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`system-tab${activeTab === tab.id ? ' system-tab--active' : ''}`}
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.id === 'overview' && overview?.attention.length ? (
              <span className="system-tab__count">{overview.attention.length}</span>
            ) : null}
          </button>
        ))}
      </nav>

      {activeTab === 'overview' ? (
        <div className="system-tab-panel" role="tabpanel">
          <section className="system-metrics-grid" aria-label="System summary">
            <MetricCard
              label="Platform health"
              value={healthState === 'loading' ? '…' : healthState === 'error' ? 'Unavailable' : humanize(health?.summary.overallStatus)}
              detail={health ? `${health.summary.healthy}/${health.summary.total} sites healthy` : undefined}
              tone={healthTone === 'healthy' ? 'healthy' : healthTone === 'degraded' ? 'warning' : healthTone === 'down' ? 'critical' : 'default'}
            />
            <MetricCard
              label="Needs attention"
              value={overviewState === 'loading' ? '…' : overviewState === 'error' ? '—' : formatNumber(overview?.attention.length)}
              detail={overview ? `${criticalSignals} critical · ${warningSignals} warning` : undefined}
              tone={criticalSignals ? 'critical' : warningSignals ? 'warning' : 'default'}
            />
            <MetricCard
              label="Open tickets"
              value={overviewState === 'loading' ? '…' : formatNumber(overview?.tickets.open)}
              detail={overview ? `${overview.tickets.unassignedOpen} unassigned · ${overview.tickets.emergencyOpen} emergency` : undefined}
              tone={overview?.tickets.emergencyOpen ? 'warning' : 'default'}
            />
            <MetricCard
              label="Open network requests"
              value={overviewState === 'loading' ? '…' : formatNumber(overview?.coreNetwork.openRequests)}
              detail={overview ? `${overview.coreNetwork.unfilledRequests} unfilled · ${overview.coreNetwork.activeMembers} active talent` : undefined}
            />
          </section>

          <section className="system-layout">
            <article className="system-card system-card--attention">
              <div className="system-card__header">
                <div>
                  <span className="system-card__eyebrow">Priority queue</span>
                  <h2>Needs Attention</h2>
                </div>
                {overview ? <span className="system-card__count">{overview.attention.length}</span> : null}
              </div>
              <div className="system-card__body system-card__body--flush">
                {overviewState === 'loading' ? <EmptyState>Loading operating signals…</EmptyState> : null}
                {overviewState === 'error' ? <EmptyState>System signals are currently unavailable.</EmptyState> : null}
                {overviewState === 'ready' && !overview?.attention.length ? <EmptyState>No active attention signals.</EmptyState> : null}
                {overview?.attention.map((signal, index) => (
                  <SignalRow key={`${signal.type}-${signal.entityId || index}`} signal={signal} />
                ))}
              </div>
            </article>
          </section>
        </div>
      ) : null}

      {activeTab === 'operations' ? (
        <div className="system-tab-panel" role="tabpanel">
          <section className="system-layout system-layout--charts">
            <article className="system-card system-card--chart">
              <div className="system-card__header">
                <div>
                  <span className="system-card__eyebrow">30-day flow</span>
                  <h2>Tickets</h2>
                </div>
                <div className="system-card__statline">
                  Median age <strong>{formatHours(overview?.tickets.medianOpenAgeHours)}</strong>
                </div>
              </div>
              <div className="system-chart">
                {overviewState === 'ready' && ticketChart.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={ticketChart} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
                      <defs>
                        <linearGradient id="ticketCreatedFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#246bff" stopOpacity={0.22} />
                          <stop offset="95%" stopColor="#246bff" stopOpacity={0.01} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8eef5" />
                      <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={24} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip labelFormatter={formatDate} />
                      <Area type="monotone" dataKey="created" name="Created" stroke="#246bff" fill="url(#ticketCreatedFill)" strokeWidth={2} />
                      <Line type="monotone" dataKey="completed" name="Completed" stroke="#20a06b" strokeWidth={2} dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState>{overviewState === 'loading' ? 'Loading ticket trend…' : 'No ticket trend data yet.'}</EmptyState>
                )}
              </div>
            </article>

            <article className="system-card system-card--chart">
              <div className="system-card__header">
                <div>
                  <span className="system-card__eyebrow">30-day flow</span>
                  <h2>Core Network</h2>
                </div>
                <div className="system-card__statline">
                  Median to assignment <strong>{formatHours(overview?.coreNetwork.medianHoursToAssignment)}</strong>
                </div>
              </div>
              <div className="system-chart">
                {overviewState === 'ready' && networkChart.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={networkChart} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8eef5" />
                      <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={24} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip labelFormatter={formatDate} />
                      <Line type="monotone" dataKey="requests" name="Requests" stroke="#7f56d9" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="completedAssignments" name="Completed" stroke="#20a06b" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState>{overviewState === 'loading' ? 'Loading network trend…' : 'No Core Network trend data yet.'}</EmptyState>
                )}
              </div>
            </article>
          </section>

          <section className="system-layout system-layout--details">
            <article className="system-card">
              <div className="system-card__header">
                <div>
                  <span className="system-card__eyebrow">Operational detail</span>
                  <h2>Ticket Health</h2>
                </div>
              </div>
              <div className="system-detail-grid">
                <MetricCard label="Completed" value={formatNumber(overview?.tickets.completedInPeriod)} detail="Last 30 days" />
                <MetricCard label="Emergency" value={formatNumber(overview?.tickets.emergencyOpen)} tone={overview?.tickets.emergencyOpen ? 'critical' : 'default'} />
                <MetricCard label="Unassigned" value={formatNumber(overview?.tickets.unassignedOpen)} tone={overview?.tickets.unassignedOpen ? 'warning' : 'default'} />
                <MetricCard label="Median open age" value={formatHours(overview?.tickets.medianOpenAgeHours)} />
              </div>
            </article>

            <article className="system-card">
              <div className="system-card__header">
                <div>
                  <span className="system-card__eyebrow">Marketplace detail</span>
                  <h2>Network Pulse</h2>
                </div>
              </div>
              <div className="system-detail-grid">
                <MetricCard label="Offers" value={formatNumber(overview?.coreNetwork.offersInPeriod)} detail="Last 30 days" />
                <MetricCard label="Assignments" value={formatNumber(overview?.coreNetwork.assignmentsInPeriod)} />
                <MetricCard label="Completed" value={formatNumber(overview?.coreNetwork.completedAssignmentsInPeriod)} />
                <MetricCard label="First offer" value={formatHours(overview?.coreNetwork.medianHoursToFirstOffer)} />
              </div>
            </article>
          </section>
        </div>
      ) : null}

      {activeTab === 'uptime' ? (
        <div className="system-tab-panel" role="tabpanel">
          <section className="system-card">
            <div className="system-card__header">
              <div>
                <span className="system-card__eyebrow">Live checks</span>
                <h2>Site Health</h2>
              </div>
            </div>
            <div className="system-card__body system-card__body--flush">
              {healthState === 'loading' ? <EmptyState>Checking Elite Core properties…</EmptyState> : null}
              {healthState === 'error' ? <EmptyState>Site health checks are currently unavailable.</EmptyState> : null}
              {health?.sites.map((site) => (
                <div className="system-site" key={site.key}>
                  <span className={`system-site__status system-site__status--${site.status}`} aria-hidden="true" />
                  <div className="system-site__identity">
                    <strong>{site.name}</strong>
                    <span>{site.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
                  </div>
                  <div className="system-site__numbers">
                    <strong>{site.statusCode || '—'}</strong>
                    <span>{site.responseTimeMs} ms</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <SiteUptimeHistory />
        </div>
      ) : null}

      {activeTab === 'ezee' ? <div className="system-tab-panel" role="tabpanel"><EzeeEmailPanel /></div> : null}

      {activeTab === 'updates' ? (
        <div className="system-tab-panel system-tab-panel--updates" role="tabpanel">
          <SystemUpdateManager onPublished={loadPublishedUpdates} />

          <section className="system-card system-card--updates">
            <div className="system-card__header">
              <div>
                <span className="system-card__eyebrow">Platform change log</span>
                <h2>Recent Updates</h2>
              </div>
            </div>
            <div className="system-card__body system-card__body--flush">
              {updatesState === 'loading' ? <EmptyState>Loading published updates…</EmptyState> : null}
              {updatesState === 'error' ? <EmptyState>Update log will appear here once the persistent update service is deployed.</EmptyState> : null}
              {updatesState === 'ready' && !updates.length ? <EmptyState>No published platform updates yet.</EmptyState> : null}
              {updates.map((update) => (
                <div className="system-update" key={update.id}>
                  <div className="system-update__date">{formatDate(update.publishedAt || update.createdAt)}</div>
                  <div className="system-update__body">
                    <div className="system-update__title-row">
                      <strong>{update.title}</strong>
                      {update.type ? <span className="system-pill">{humanize(update.type)}</span> : null}
                    </div>
                    {update.summary ? <p>{update.summary}</p> : null}
                    <div className="system-update__meta">
                      {update.version ? <span>v{update.version}</span> : null}
                      {update.pullRequestNumber ? <span>PR #{update.pullRequestNumber}</span> : null}
                      {update.gitCommitSha ? <span>{update.gitCommitSha.slice(0, 7)}</span> : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
};

export default SystemOperationsPage;
